/* eslint-disable global-require */
import React from 'react';
import { hydrate } from 'react-dom';
import { compose } from 'redux';
import { Provider as ReduxProvider } from 'react-redux';
import { withRouter, BrowserRouter } from 'react-router-dom';
import { ApolloClient } from 'apollo-client';
import {
  InMemoryCache,
  IntrospectionFragmentMatcher
} from 'apollo-cache-inmemory';
import { ApolloProvider } from '@apollo/react-common';
import { ApolloLink } from 'apollo-link';
import { createGaClient, DataLayer } from '@redbubble/boom-analytics';
import {
  disableGa,
  enableGa
} from '@redbubble/boom-analytics/lib/redux/actions';
import {
  SessionContextProvider,
  SessionContextConsumer
} from '@redbubble/boom-session-context';
import { CookiesProvider, withCookies } from 'react-cookie';
import Cookies from 'universal-cookie';
import Loadable from 'react-loadable';
import { IntlProvider } from 'react-intl';
import SegmentProvider from '@redbubble/react-segment/SegmentProvider';
import config from '../config';
import configureStore from '../shared/redux/configureStore';
import ReactHotLoader from './components/ReactHotLoader';
import App from '../shared/App';
import withUserInfo from '../shared/containers/apollo/withUserInfo';
import withAnalytics from '../shared/containers/redux/withAnalytics';
import withUserLocation from '../shared/containers/redux/withUserLocation';
import {
  cacheRedirects,
  defaultOptions,
  buildLink,
  clientErrorLink
} from '../shared/apollo';
import ErrorBoundary from '../shared/components/ErrorBoundary';
import { clientError } from '../shared/lib/errorReporting';

// react-intl locale for supported languages
import './i18n/supportedLanguages';

// Set Webpack's publicPath based on CDN_BASE_URL
// This is done at runtime to avoid hard-coding environment specifics
// into our assets.
// For more information see https://webpack.js.org/guides/public-path/#on-the-fly.
if (config('environment') !== 'development') {
  // eslint-disable-next-line no-underscore-dangle, camelcase, no-undef
  __webpack_public_path__ = config('publicClientBundleUrl');
}

// Get the DOM Element that will host our React application.
const container = document.querySelector('#app');

// Does the user's browser support the HTML5 history API?
// If the user's browser doesn't support the HTML5 history API then we
// will force full page refreshes on each page change.
const supportsHistory = 'pushState' in window.history;

/* eslint-disable no-underscore-dangle */
const locale = window.__CURRENT_LOCALE__;
const apolloState = window.__APOLLO_STATE__;
const initialState = window.__APP_STATE__;
const fragmentTypes = window.__APOLLO_FRAGMENT_TYPES__;
const localizedMessages = window.__LOCALIZED_MESSAGES__;
/* eslint-enable no-underscore-dangle */

const link = buildLink({ 'x-locale': locale });
const links = [clientErrorLink, link];

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData: fragmentTypes
});

const apolloClient = new ApolloClient({
  link: ApolloLink.from(links),
  cache: new InMemoryCache({ cacheRedirects, fragmentMatcher }).restore(
    apolloState
  ),
  ssrForceFetchDelay: 100,
  defaultOptions
});

const gaAccountId = config('gaAccount');
const storeConfig = {};

if (gaAccountId && gaAccountId !== '') {
  const analyticsClient = createGaClient(gaAccountId);
  analyticsClient.require('ec');
  storeConfig.analyticsClient = analyticsClient;
  storeConfig.dataLayer = new DataLayer(window.dataLayer);
}

const store = configureStore(initialState, storeConfig);

const intialConsentState = {
  necessary: true,
  preferences: false,
  statistics: false,
  marketing: false
};

const useCookiebotConsent = (onAccept = () => {}, onDisable = () => {}) => {
  const [consent, setConsent] = React.useState(intialConsentState);

  function handleCookiebotAccept() {
    // eslint-disable-next-line no-undef
    const { necessary, preferences, statistics, marketing } = Cookiebot.consent;
    if (
      consent.necessary !== necessary ||
      consent.preferences !== preferences ||
      consent.statistics !== statistics ||
      consent.marketing !== marketing
    ) {
      setConsent({ necessary, preferences, statistics, marketing });
    }
    onAccept();
  }

  function handleCookiebotDisable() {
    // eslint-disable-next-line no-undef
    const { necessary, preferences, statistics, marketing } = Cookiebot.consent;
    if (
      consent.necessary !== necessary ||
      consent.preferences !== preferences ||
      consent.statistics !== statistics ||
      consent.marketing !== marketing
    ) {
      setConsent({ necessary, preferences, statistics, marketing });
    }
    onDisable();
  }

  React.useEffect(() => {
    // Add event listeners for Cookiebot events
    window.addEventListener('CookiebotOnAccept', handleCookiebotAccept, false);
    window.addEventListener(
      'CookiebotOnDecline',
      handleCookiebotDisable,
      false
    );

    return () => {
      window.removeEventListener(
        'CookiebotOnAccept',
        handleCookiebotAccept,
        false
      );
      window.removeEventListener(
        'CookiebotOnDecline',
        handleCookiebotDisable,
        false
      );
    };
  }, [consent]);

  return consent;
};

/**
 * Renders the given React Application component.
 */
function renderApp(TheApp, reduxStore) {
  // withRouter needs to be first, otherwise, routing breaks
  const ComposedApp = compose(
    withRouter,
    withUserInfo,
    withCookies,
    withAnalytics,
    withUserLocation
  )(App);

  // Firstly, define our full application component, wrapping the given
  // component app with a browser based version of react router.
  const AppWithProviders = () => {
    const consent = useCookiebotConsent();

    React.useEffect(() => {
      if (consent.statistics) {
        store.dispatch(enableGa());
      } else if (
        !consent.statistics &&
        !!storeConfig.analyticsClient &&
        storeConfig.analyticsClient.isEnabled()
      ) {
        store.dispatch(disableGa());
      }
    }, [consent]);

    return (
      <ReactHotLoader>
        <ReduxProvider store={reduxStore}>
          <CookiesProvider cookies={new Cookies(reduxStore.cookies)}>
            <ApolloProvider client={apolloClient}>
              <SessionContextProvider isServer={false}>
                <IntlProvider locale={locale} messages={localizedMessages}>
                  <BrowserRouter forceRefresh={!supportsHistory}>
                    <ErrorBoundary>
                      <SessionContextConsumer>
                        {() => {
                          return (
                            <SegmentProvider
                              enabled={false}
                              writeKey={config('segmentWriteKey')}
                              cdnSourceDomain={config('segmentCdnDomain')}
                              onError={clientError}
                            >
                              <ComposedApp />
                            </SegmentProvider>
                          );
                        }}
                      </SessionContextConsumer>
                    </ErrorBoundary>
                  </BrowserRouter>
                </IntlProvider>
              </SessionContextProvider>
            </ApolloProvider>
          </CookiesProvider>
        </ReduxProvider>
      </ReactHotLoader>
    );
  };

  Loadable.preloadReady().then(() => {
    hydrate(<AppWithProviders />, container);
  });
}

// Execute the first render of our app.
renderApp(App, store);

// The following is needed so that we can support hot reloading our application.
if (process.env.BUILD_FLAG_IS_DEV === 'true' && module.hot) {
  // Accept changes to this file for hot reloading.
  module.hot.accept('./index.js');
  // Any changes to our App will cause a hotload re-render.
  module.hot.accept('../shared/App', () => {
    renderApp(require('../shared/App').default, store);
  });
}
